import { decode } from 'html-entities';
import { formatOgImage } from '@benzinga/frontend-utils';
import type { ArticleBlock, ArticleBlockPlaceholder, ArticleData, VideoContentDetails } from '../entities/article';

export const VIDEO_ARTICLE_STORY_TYPE = 'bz_watch';
export const LEGACY_VIDEO_ARTICLE_STORY_TYPE = 'video';

const VIDEO_BLOCK_NAMES = new Set(['core-embed/youtube', 'acf/wistia-player', 'acf/youtube-list', 'acf/videos']);
const YOUTUBE_ID_REGEX = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
const BENZINGA_IMAGE_HOST_PATTERN = /(?:^https?:\/\/)?(?:www\.|cdn\.)?benzinga\.com/i;

export const getVideoPostMeta = (article: ArticleData | null | undefined) => article?.meta?.wp_post_meta;

const getArticleBlocks = (article: ArticleData): ArticleBlock[] => {
  if (!Array.isArray(article.blocks)) return [];
  return article.blocks.filter((block): block is ArticleBlock => 'innerHTML' in block);
};

export const isVideoBlock = (block: ArticleBlock | ArticleBlockPlaceholder): boolean => {
  if (!block?.blockName) return false;
  if (VIDEO_BLOCK_NAMES.has(block.blockName)) return true;
  if (!('innerHTML' in block)) return false;
  if (block.blockName === 'core/embed' && block.attrs?.providerNameSlug === 'youtube') return true;
  if (typeof block.innerHTML === 'string' && block.innerHTML.includes('youtube.com/embed')) return true;
  return false;
};

export const isVideoArticle = (article: ArticleData | null | undefined): boolean => {
  if (!article) return false;
  if (article.type === VIDEO_ARTICLE_STORY_TYPE || article.type === LEGACY_VIDEO_ARTICLE_STORY_TYPE) return true;

  const videoMeta = getVideoPostMeta(article);
  if (videoMeta?.post_type === VIDEO_ARTICLE_STORY_TYPE) return true;
  if (videoMeta?.VideoID || videoMeta?.YouTubeURL) return true;

  const flags = article.meta?.Flags as unknown as { VideoArticle?: boolean } | null | undefined;
  if (flags?.VideoArticle) return true;

  return article.channels?.some(channel => channel.name?.toLowerCase() === 'video') ?? false;
};

export const extractVideoArticleBlock = (article: ArticleData): ArticleBlock | null => {
  return getArticleBlocks(article).find(isVideoBlock) ?? null;
};

const parseYoutubeIdFromUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  const match = url.match(YOUTUBE_ID_REGEX);
  return match?.[2]?.length === 11 ? match[2] : null;
};

const extractYoutubeIdFromBlock = (videoBlock: ArticleBlock): string | null => {
  const directVideoId = (videoBlock as unknown as { videoId?: string }).videoId;
  if (directVideoId) {
    return directVideoId;
  }

  const fromUrl = parseYoutubeIdFromUrl(videoBlock.attrs?.url as string | undefined);
  if (fromUrl) {
    return fromUrl;
  }

  const iframeSrcMatch = videoBlock.innerHTML?.match(/<iframe[^>]*?\bsrc="([^"]*)"[^>]*>/);
  return parseYoutubeIdFromUrl(iframeSrcMatch?.[1]);
};

export const getVideoArticleYoutubeId = (article: ArticleData): string | null => {
  const videoMeta = getVideoPostMeta(article);
  if (videoMeta?.VideoID) {
    return videoMeta.VideoID;
  }

  const fromYouTubeUrl = parseYoutubeIdFromUrl(videoMeta?.YouTubeURL);
  if (fromYouTubeUrl) {
    return fromYouTubeUrl;
  }

  const videoBlock = extractVideoArticleBlock(article);
  return videoBlock ? extractYoutubeIdFromBlock(videoBlock) : null;
};

export const getVideoArticleTitle = (article: ArticleData): string => {
  return getVideoPostMeta(article)?.VideoTitle?.trim() || article.title || 'Benzinga video';
};

export const getVideoArticleImage = (article: ArticleData): string | undefined => {
  const videoMeta = getVideoPostMeta(article);
  return videoMeta?.VideoThumbnail || article.image || undefined;
};

export const getVideoArticleSummary = (article: ArticleData): string => {
  const rawSummary = article.teaserText?.trim()
    ? article.teaserText.trim()
    : getArticleBlocks(article)
        .filter(block => !isVideoBlock(block))
        .map(block => block.innerHTML ?? '')
        .join('')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

  return rawSummary ? decode(rawSummary) : rawSummary;
};

const isBenzingaImageUrl = (imageUrl: string): boolean => {
  return BENZINGA_IMAGE_HOST_PATTERN.test(imageUrl) || imageUrl.startsWith('/');
};

const getYoutubeThumbnailUrls = (youtubeId: string): string[] => {
  const base = `https://i.ytimg.com/vi/${youtubeId}`;
  return [`${base}/maxresdefault.jpg`, `${base}/hqdefault.jpg`];
};

export const formatVideoThumbnailUrl = (
  thumbnailUrl: string | undefined,
  youtubeId: string | null,
): string | string[] | undefined => {
  if (youtubeId) {
    return getYoutubeThumbnailUrls(youtubeId);
  }

  if (!thumbnailUrl) {
    return undefined;
  }

  if (thumbnailUrl.includes('ytimg.com') || thumbnailUrl.includes('youtube.com')) {
    return thumbnailUrl;
  }

  if (isBenzingaImageUrl(thumbnailUrl)) {
    return formatOgImage(thumbnailUrl);
  }

  return thumbnailUrl;
};

const secondsToIso8601Duration = (totalSeconds: number): string | undefined => {
  if (totalSeconds <= 0) {
    return undefined;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  let duration = 'PT';
  if (hours > 0) {
    duration += `${hours}H`;
  }
  if (minutes > 0) {
    duration += `${minutes}M`;
  }
  if (seconds > 0 || duration === 'PT') {
    duration += `${seconds}S`;
  }

  return duration;
};

const parseYoutubeDurationToIso8601 = (duration: string | undefined | null): string | undefined => {
  if (!duration || duration === 'P0D') {
    return undefined;
  }

  if (duration.startsWith('PT')) {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) {
      return undefined;
    }

    const hours = parseInt(match[1] ?? '0', 10);
    const minutes = parseInt(match[2] ?? '0', 10);
    const seconds = parseInt(match[3] ?? '0', 10);
    return secondsToIso8601Duration(hours * 3600 + minutes * 60 + seconds);
  }

  return undefined;
};

const getVideoDurationIso8601 = (
  videoMeta: { VideoDuration?: string; contentDetails?: VideoContentDetails } | undefined,
): string | undefined => {
  const rawDuration = videoMeta?.VideoDuration ?? videoMeta?.contentDetails?.duration;
  return parseYoutubeDurationToIso8601(rawDuration);
};

export const buildVideoObjectSchema = (
  article: ArticleData,
  createdAtISO: string | null | undefined,
  description: string,
): Record<string, unknown> | null => {
  if (!isVideoArticle(article)) return null;

  const videoMeta = getVideoPostMeta(article);
  const youtubeId = getVideoArticleYoutubeId(article);
  const thumbnailUrl = getVideoArticleImage(article);
  const videoSummary = getVideoArticleSummary(article);
  const name = decode(getVideoArticleTitle(article));
  const formattedThumbnail = formatVideoThumbnailUrl(thumbnailUrl, youtubeId);
  const duration = getVideoDurationIso8601(videoMeta);

  if (!youtubeId && !formattedThumbnail) return null;

  const videoObject: Record<string, unknown> = {
    '@type': 'VideoObject',
    description: videoSummary || description,
    name,
    ...(createdAtISO && { uploadDate: createdAtISO }),
    ...(formattedThumbnail && { thumbnailUrl: formattedThumbnail }),
    ...(duration && { duration }),
  };

  if (youtubeId) {
    videoObject.embedUrl = `https://www.youtube.com/embed/${youtubeId}`;
  }

  return videoObject;
};
